import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPlacemarks, goTo as goToRoute } from '@/shared/actions';
import { getCoordinatesForAuthority } from '../../newReport/actions';
import { getFeatureCollectionMarkers } from '../MyCityHelper';
import {
  getPOISForAuthority,
  getReportsForAuthority,
  setFilters,
  subscribeToPoi,
  unsubscribeFromPoi,
} from '../actions';
import {
  DEFAULT_FILTERS,
  MIN_ZOOM,
  buildBaseCollection,
  createLoaderStyles,
  estimateZoomFromBounds,
  filterFeatureCollection,
  getBoundsFromFeatures,
  getFitBoundsFromPlacemarks,
  isPointInsideBounds,
  selectMyCityState,
} from '../utils';

const getInitialIsMobileView = () =>
  typeof window !== 'undefined' ? window.innerWidth < 768 : false;

export const useMyCityData = () => {
  const dispatch = useDispatch();
  const {
    reports,
    pois,
    activeFilters,
    adoptATreeModuleEnabled,
    placemarks,
    authorityName,
    countryCode,
  } = useSelector(selectMyCityState);

  const [center, setCenter] = useState([]);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [markersLoaded, setMarkersLoaded] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [userGpsLocation, setUserGpsLocation] = useState(null);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [authorityBounds, setAuthorityBounds] = useState(null);
  const [markerBounds, setMarkerBounds] = useState(null);
  const [isMobileView, setIsMobileView] = useState(getInitialIsMobileView);
  const zoom = useMemo(() => [MIN_ZOOM], []);
  const loaderStyles = useMemo(() => createLoaderStyles(), []);
  const fitBounds = useMemo(
    () => markerBounds || authorityBounds,
    [markerBounds, authorityBounds]
  );

  const dataRequestIdRef = useRef(0);
  const defaultCenterRef = useRef(null);
  const [defaultFitBounds, setDefaultFitBounds] = useState(null);
  const [defaultViewZoom, setDefaultViewZoom] = useState(MIN_ZOOM);
  const viewportSizeRef = useRef({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
      viewportSizeRef.current = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    dispatch(setFilters(DEFAULT_FILTERS));
  }, [dispatch]);

  const baseCollection = useMemo(
    () => buildBaseCollection(reports, pois),
    [reports, pois]
  );

  const markerHtmlArray = useMemo(
    () => getFeatureCollectionMarkers(baseCollection),
    [baseCollection]
  );

  const featureCollectionPoints = useMemo(
    () => filterFeatureCollection(baseCollection, activeFilters),
    [baseCollection, activeFilters]
  );

  const featureCollection = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: featureCollectionPoints,
    }),
    [featureCollectionPoints]
  );

  useEffect(() => {
    setShowInfoWindow(false);
  }, [activeFilters]);

  useEffect(() => {
    if (!authorityName) {
      return;
    }

    dataRequestIdRef.current += 1;
    const requestId = dataRequestIdRef.current;
    setMarkersLoaded(false);
    setShowInfoWindow(false);
    setSelectedPoint(null);
    setAuthorityBounds(null);
    setMarkerBounds(null);

    const fetchData = async () => {
      try {
        const responses = await Promise.all([
          dispatch(
            getPOISForAuthority({ includeTrees: adoptATreeModuleEnabled })
          ),
          dispatch(getReportsForAuthority()),
          getCoordinatesForAuthority(authorityName, countryCode),
        ]);

        if (dataRequestIdRef.current !== requestId) {
          return;
        }

        const coordinates = responses[2];
        const lng = parseFloat(coordinates?.longitude);
        const lat = parseFloat(coordinates?.latitude);

        if (Number.isFinite(lng) && Number.isFinite(lat)) {
          const newCenter = [lng, lat];
          setCenter(newCenter);
          setUserLocation({ lat, lng });
          defaultCenterRef.current = newCenter;
        }

        setMarkersLoaded(true);
      } catch (error) {
        console.error('Failed to load MyCity data', error);
        if (dataRequestIdRef.current === requestId) {
          setMarkersLoaded(true);
        }
      }
    };

    fetchData();
    dispatch(getPlacemarks({ authority: authorityName }));
  }, [authorityName, countryCode, adoptATreeModuleEnabled, dispatch]);

  useEffect(() => {
    if (!placemarks?.features?.length || center.length !== 2) {
      return;
    }

    const bounds = getFitBoundsFromPlacemarks(placemarks);
    if (!bounds) return;
    if (!isPointInsideBounds(bounds, center)) {
      return;
    }

    setAuthorityBounds(bounds);
    setDefaultFitBounds(bounds);
    setDefaultViewZoom(
      estimateZoomFromBounds(bounds, viewportSizeRef.current)
    );
  }, [placemarks, center]);

  useEffect(() => {
    if (
      !featureCollectionPoints?.length ||
      featureCollectionPoints.length < 2
    ) {
      setMarkerBounds(null);
      return;
    }

    const bounds = getBoundsFromFeatures(featureCollectionPoints);
    if (!bounds) {
      setMarkerBounds(null);
      return;
    }

    setMarkerBounds(bounds);
    setDefaultFitBounds(bounds);
    setDefaultViewZoom(
      estimateZoomFromBounds(bounds, viewportSizeRef.current)
    );
  }, [featureCollectionPoints]);

  const openInfoWindow = useCallback((event) => {
    const coordinates = event?.features?.[0]?.geometry?.coordinates?.slice();
    const rawPoint = event?.features?.[0]?.properties?.point;
    const parsedPoint =
      typeof rawPoint === 'string' ? JSON.parse(rawPoint) : rawPoint;

    if (!coordinates || !parsedPoint) {
      return;
    }

    setCenter(coordinates);
    setSelectedPoint(parsedPoint);
    setShowInfoWindow(true);
  }, []);

  const closeInfoWindow = useCallback(() => {
    setShowInfoWindow(false);
  }, []);

  const onClickCluster = useCallback((event) => {
    const { lng, lat } = event?.lngLat || {};
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      return;
    }
    setCenter([lng, lat]);
  }, []);

  const zoomControlClick = useCallback(() => {
    if (!selectedPoint) return;
    const lng = parseFloat(selectedPoint.longitude);
    const lat = parseFloat(selectedPoint.latitude);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      return;
    }
    setShowInfoWindow(false);
    setCenter([lng, lat]);
  }, [selectedPoint]);

  const handleFiltersChanged = useCallback(
    (selectedPointType, filterKey = 'name') => {
      if (!selectedPointType) return;

      let updatedFilters = [...activeFilters];
      const index = updatedFilters.findIndex(
        (f) => f[filterKey] === selectedPointType
      );
      const isStatus = filterKey === 'status_name';
      const hasReportsFilter = updatedFilters.some((f) => f.name === 'REPORTS');

      if (index === -1) {
        updatedFilters.push({ [filterKey]: selectedPointType });
        if (isStatus && !hasReportsFilter) {
          updatedFilters.push({ name: 'REPORTS' });
        }
      } else {
        updatedFilters.splice(index, 1);
        if (isStatus) {
          const remainingStatus = updatedFilters.some((f) => f.status_name);
          if (!remainingStatus && hasReportsFilter) {
            updatedFilters = updatedFilters.filter((f) => f.name !== 'REPORTS');
          }
        }
      }

      const hasNonReportsFilters = updatedFilters.some(
        (f) => f.name && f.name !== 'REPORTS'
      );
      const hasStatusFilters = updatedFilters.some((f) => f.status_name);

      if (!hasNonReportsFilters && !hasStatusFilters) {
        dispatch(setFilters([]));
        return;
      }

      dispatch(setFilters(updatedFilters));
    },
    [activeFilters, dispatch]
  );

  const clearFilters = useCallback(() => {
    dispatch(setFilters([]));
    setShowInfoWindow(false);
    setSelectedPoint(null);
  }, [dispatch]);

  const goTo = useCallback(
    (page, urlData, reducerData) => {
      dispatch(goToRoute(page, urlData, reducerData));
    },
    [dispatch]
  );

  const handleSubscribeToPoi = useCallback(
    (poi) => dispatch(subscribeToPoi(poi)),
    [dispatch]
  );

  const handleUnsubscribeFromPoi = useCallback(
    (poi) => dispatch(unsubscribeFromPoi(poi)),
    [dispatch]
  );

  const resetView = useCallback(() => {
    if (!defaultCenterRef.current && !defaultFitBounds) {
      return;
    }

    if (defaultCenterRef.current) {
      setCenter(defaultCenterRef.current);
    }

    setSelectedPoint(null);
    setShowInfoWindow(false);
  }, [defaultFitBounds]);

  const requestUserLocation = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      console.warn('Geolocation is not supported by this browser');
      return;
    }

    if (isRequestingLocation) {
      return;
    }

    setIsRequestingLocation(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const lat = parseFloat(coords?.latitude);
        const lng = parseFloat(coords?.longitude);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          setIsRequestingLocation(false);
          return;
        }

        const nextCenter = [lng, lat];
        setCenter(nextCenter);
        setUserGpsLocation({ lat, lng });
        setSelectedPoint(null);
        setShowInfoWindow(false);
        // temporarily override fit bounds zoom so we can zoom on user
        setMarkerBounds(null);
        setAuthorityBounds(null);
        setIsRequestingLocation(false);
      },
      (error) => {
        console.error('Failed to acquire user location', error);
        setIsRequestingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, [isRequestingLocation]);

  const mapProps = useMemo(
    () => ({
      goTo,
      isFromCity: true,
      zoom,
      center,
      subscribeToPoi: handleSubscribeToPoi,
      markerHtmlArray,
      userLocation,
      featureCollection,
      unsubscribeFromPoi: handleUnsubscribeFromPoi,
      selectedPoint: selectedPoint || {},
      showInfoWindow,
      onClickCluster,
      openInfoWindow,
      closeInfoWindow,
      zoomControlClick,
      placemarks,
      fitBounds,
      resetView,
      defaultFitBounds,
      defaultViewZoom,
      userGpsLocation,
      requestUserLocation,
      isRequestingLocation,
    }),
    [
      goTo,
      zoom,
      center,
      handleSubscribeToPoi,
      markerHtmlArray,
      userLocation,
      featureCollection,
      handleUnsubscribeFromPoi,
      selectedPoint,
      showInfoWindow,
      onClickCluster,
      openInfoWindow,
      closeInfoWindow,
      zoomControlClick,
      placemarks,
      fitBounds,
      resetView,
      defaultFitBounds,
      defaultViewZoom,
      userGpsLocation,
      requestUserLocation,
      isRequestingLocation,
    ]
  );

  const filtersProps = useMemo(
    () => ({
      reports,
      pois,
      activeFilters,
      filtersChanged: handleFiltersChanged,
      clearFilters,
    }),
    [reports, pois, activeFilters, handleFiltersChanged, clearFilters]
  );

  const resultsCount = featureCollectionPoints.length;

  return {
    isMobileView,
    markersLoaded,
    loaderStyles,
    mapProps,
    filtersProps,
    resultsCount,
    activeFilters,
    resetView,
    defaultFitBounds,
    defaultViewZoom,
    userGpsLocation,
    requestUserLocation,
    isRequestingLocation,
  };
};

export default useMyCityData;
